import { TreeItem } from '@graftcode/design-system'

import { METHOD_TYPE_BADGE_LABELS } from '@/core/const/methodTypeBadgeLabels.const'
import { ClassData } from '@/core/types/library.type'
import { createTreeBadgeContent } from '@/core/utils/createTreeBadgeContent'
import { GLOBAL_ENTRY_SUFFIX } from '@/core/utils/mappers/const/globalEntrySuffix.const'

import { mapClassItemToTreeItem } from './mapClassItemToTreeItem'
import { mapFieldToTreeItem } from './mapFieldToTreeItem'

/**
 * Maps ClassData to a TreeItem with nested children: nested types, methods, fields, constructors. Used for sidebar hierarchy.
 *
 * @param itemKey - Optional key for nested children with duplicate names; constructors use it for overloads
 */
export const mapClassToTreeItem = (item: ClassData, parentPath: string, itemKey?: string): TreeItem => {
  const uniqueId = itemKey || item.name
  const id = `${parentPath}/${uniqueId}`

  if (itemKey?.endsWith(GLOBAL_ENTRY_SUFFIX.METHOD) && item.methods && Object.keys(item.methods).length === 1) {
    const firstMethodEntry = Object.entries(item.methods)[0]
    if (firstMethodEntry) {
      const [methodKey, method] = firstMethodEntry
      return mapClassItemToTreeItem(method, id, methodKey)
    }
  }

  if (itemKey?.endsWith(GLOBAL_ENTRY_SUFFIX.FIELD) && item.fields && Object.keys(item.fields).length === 1) {
    const firstField = Object.values(item.fields)[0]
    if (firstField) {
      return mapFieldToTreeItem(firstField, id)
    }
  }

  const children: TreeItem[] = []

  if (item.children) {
    children.push(...Object.entries(item.children).map(([key, child]) => mapClassToTreeItem(child, id, key)))
  }

  if (item.methods) {
    children.push(...Object.entries(item.methods).map(([key, child]) => mapClassItemToTreeItem(child, id, key)))
  }

  if (item.fields) {
    children.push(...Object.values(item.fields).map((child) => mapFieldToTreeItem(child, id)))
  }

  if (item.constructors) {
    children.push(...Object.entries(item.constructors).map(([key, child]) => mapClassItemToTreeItem(child, id, key)))
  }

  return {
    id,
    label: item.name,
    type: children.length === 0 ? 'leaf' : undefined,
    children: children.length > 0 ? children : undefined,
    badge: createTreeBadgeContent(METHOD_TYPE_BADGE_LABELS.CLASS) as unknown as TreeItem['badge'],
  }
}
